import { observer } from 'mobx-react-lite'
import { SingleItemSelector } from '@/components/SingleItemSelector'
import { Avatar } from '@/components/Avatar'
import { FormColumn, FormColumns, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { match } from '@/utils/pattern-match-util'
import { AdvisorSelectContainer } from '@/features/iam/invitations/components/AdvisorSelectContainer'
import {
  BulkUploadForm,
  EntryModeSelector,
  SignInTypeSelector,
  InvitationDialog,
  InvitationResultsDialog,
} from '@/features/iam/invitations/components/InvitationDialog'
import { downloadCustomerCsvTemplate } from '@/features/iam/customers/customer-csv'
import { getPortalDisplayName } from '@/features/user-management/portal-display'
import { type WorkspaceInvitationDialogViewModel } from './WorkspaceInvitationDialogViewModel'
import { SkillsSelectorContainer } from '@/features/user-management/components/SkillsSelectorContainer'

export interface WorkspaceInvitationDialogContainerProps {
  vm: WorkspaceInvitationDialogViewModel
}

export const WorkspaceInvitationDialogContainer = observer(
  function WorkspaceInvitationDialogContainer({
    vm,
  }: WorkspaceInvitationDialogContainerProps) {
    const testId = 'workspace-invitation'

    if (vm.showBulkResults) {
      return (
        <InvitationResultsDialog
          testId={testId}
          showing={vm.modal.showing}
          onDone={vm.dismissBulkResults}
          onRetryFailed={vm.retryFailedBulkResults}
          successfulResults={vm.successfulBulkResults.map((result) => ({
            ...result,
          }))}
          failedResults={vm.failedBulkResults.map((result) => ({
            ...result,
          }))}
        />
      )
    }

    return (
      <InvitationDialog
        title="Invite to workspace"
        description={vm.dialogDescription}
        testId={testId}
        showing={vm.modal.showing}
        onClose={vm.close}
        signInType={vm.signInType}
        bulkUploadMode={vm.bulkUploadMode}
        validBulkCustomerCount={vm.validBulkCustomers.length}
        emailAddressCount={vm.emailAddresses.length}
        onSubmit={vm.submit}
        isSubmitPending={vm.submit.pending}
      >
        <FormRow>
          <FormGroupContainer
            label="Workspace"
            error={
              vm.signInType === 'AccessLink'
                ? vm.customerValidation.getError('workspace')
                : vm.invitationValidation.getError('workspace')
            }
          >
            <SingleItemSelector
              value={vm.workspace}
              itemOptions={vm.workspaces}
              itemPrefix={(item) => (
                <Avatar
                  subject={{
                    avatar: item?.branding.logos.icon,
                  }}
                  size={24}
                />
              )}
              onChange={vm.setWorkspace}
              itemToString={(item) => (item ? item.name : '')}
              placeholder="Select workspace"
              isDisabled={vm.submit.pending}
              testId={`${testId}-workspace-selector`}
            />
          </FormGroupContainer>
        </FormRow>

        {vm.workspace &&
          vm.canUseAccessLink &&
          vm.canUseUsernameAndPassword && (
            <SignInTypeSelector
              signInType={vm.signInType}
              onSignInTypeChange={vm.setSignInType}
              isDisabled={vm.submit.pending}
            />
          )}

        {match(vm.signInType, {
          AccessLink: () => (
            <CustomerInvitationFormContainer vm={vm} testId={testId} />
          ),
          UsernamePassword: () => (
            <RegularInvitationFormContainer vm={vm} testId={testId} />
          ),
        })}

        {vm.canChooseAdvisor && vm.signInType === 'AccessLink' && (
          <AdvisorSelectContainer
            vm={vm.advisorSelect}
            isDisabled={vm.submit.pending}
          />
        )}
      </InvitationDialog>
    )
  },
)

interface InvitationFormContainerProps {
  vm: WorkspaceInvitationDialogViewModel
  testId: string
}

const CustomerInvitationFormContainer = observer(
  function CustomerInvitationFormContainer({
    vm,
    testId,
  }: InvitationFormContainerProps) {
    return (
      <>
        <EntryModeSelector
          bulkUploadMode={vm.bulkUploadMode}
          onBulkUploadModeChange={vm.setBulkUploadMode}
          isDisabled={vm.submit.pending}
        />

        {vm.bulkUploadMode ? (
          <BulkUploadForm
            testId={testId}
            fileName={vm.bulkUploadFileName}
            error={vm.bulkUploadError}
            customers={vm.bulkCustomers.map((customer) => ({
              ...customer,
              errors: customer.errors.slice(),
            }))}
            validCustomerCount={vm.validBulkCustomers.length}
            isDisabled={vm.submit.pending}
            onFileSelected={vm.handleCsvUpload}
            onRemoveCustomer={vm.removeBulkCustomer}
            onDownloadTemplate={downloadCustomerCsvTemplate}
          />
        ) : (
          <SingleCustomerFormContainer vm={vm} testId={testId} />
        )}
      </>
    )
  },
)

const SingleCustomerFormContainer = observer(
  function SingleCustomerFormContainer({
    vm,
    testId,
  }: InvitationFormContainerProps) {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.customerValidation.getError('customerEmail')}
          >
            <TextInputContainer
              model={vm}
              field="customerEmail"
              type="email"
              placeholder="customer@example.com"
              isDisabled={vm.submit.pending}
              data-test={`${testId}-customer-email`}
            />
          </FormGroupContainer>
        </FormRow>

        <FormRow>
          <FormColumns>
            <FormColumn>
              <FormGroupContainer
                label="First name"
                error={vm.customerValidation.getError('customerGivenName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerGivenName"
                  placeholder="John"
                  isDisabled={vm.submit.pending}
                  data-test={`${testId}-customer-given-name`}
                />
              </FormGroupContainer>
            </FormColumn>
            <FormColumn>
              <FormGroupContainer
                label="Last name"
                error={vm.customerValidation.getError('customerFamilyName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerFamilyName"
                  placeholder="Doe"
                  isDisabled={vm.submit.pending}
                  data-test={`${testId}-customer-family-name`}
                />
              </FormGroupContainer>
            </FormColumn>
          </FormColumns>
        </FormRow>

        <FormRow>
          <FormGroupContainer
            label="Phone number"
            sublabel="(optional)"
            error={vm.customerValidation.getError('customerPhoneNumber')}
          >
            <TextInputContainer
              model={vm}
              field="customerPhoneNumber"
              type="tel"
              placeholder="+1 555-123-4567"
              isDisabled={vm.submit.pending}
              data-test={`${testId}-customer-phone`}
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)

const RegularInvitationFormContainer = observer(
  function RegularInvitationFormContainer({
    vm,
    testId,
  }: InvitationFormContainerProps) {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.invitationValidation.getError('emailAddressTextArea')}
          >
            <TextAreaContainer
              model={vm}
              field="emailAddressTextArea"
              rows={3}
              placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
              help="Enter one or more email addresses, separated by commas."
              data-test={`${testId}-email-textarea`}
            />
          </FormGroupContainer>
        </FormRow>

        {vm.workspace && (
          <>
            <FormRow>
              <FormGroupContainer
                label="Role"
                error={vm.invitationValidation.getError('workspaceRole')}
              >
                <SingleItemSelector
                  value={vm.workspaceRole}
                  itemOptions={vm.workspaceRoles}
                  onChange={vm.setWorkspaceRole}
                  itemToString={(item) => (item ? item.name : '')}
                  placeholder="Select workspace role"
                  isDisabled={vm.submit.pending}
                  testId={`${testId}-role-selector`}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Skills" sublabel="(optional)">
                <SkillsSelectorContainer
                  vm={vm}
                  isDisabled={vm.submit.pending}
                  testId={`${testId}-skills-selector`}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Team" sublabel="(optional)">
                <SingleItemSelector
                  value={vm.team}
                  itemOptions={vm.teams}
                  onChange={vm.setTeam}
                  itemToString={(item) => (item ? item.name : '')}
                  placeholder="Select team"
                  isDisabled={vm.submit.pending}
                  testId={`${testId}-team-selector`}
                />
              </FormGroupContainer>
            </FormRow>

            {vm.team && (
              <FormRow>
                <FormGroupContainer
                  label="Team role"
                  error={vm.invitationValidation.getError('teamRole')}
                >
                  <SingleItemSelector
                    value={vm.teamRole}
                    itemOptions={vm.teamRoles}
                    onChange={vm.setTeamRole}
                    itemToString={(item) => (item ? item.name : '')}
                    placeholder="Select team role"
                    isDisabled={vm.submit.pending}
                    testId={`${testId}-team-role-selector`}
                  />
                </FormGroupContainer>
              </FormRow>
            )}
          </>
        )}

        <FormRow>
          <FormGroupContainer label="Portal">
            <SingleItemSelector
              value={vm.portal}
              itemOptions={vm.portals}
              onChange={vm.setPortal}
              itemToString={getPortalDisplayName}
              placeholder="Select portal to link to"
              isDisabled={vm.submit.pending}
              testId={`${testId}-portal-selector`}
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)
